
import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Thermometer, Fan, AlertTriangle, Check } from 'lucide-react';

interface ChillerDetailCardProps {
  id: string | number;
}

const ChillerDetailCard: React.FC<ChillerDetailCardProps> = ({ id }) => {
  // Mock data for each specific chiller
  const chillerData = {
    "1": {
      name: `Chiller ${id}`,
      manufacturer: 'CarrierTech',
      model: 'CT-5500',
      status: 'healthy',
      lastMaintenance: '2023-10-15',
      nextMaintenance: '2024-04-15',
      refrigerantLevel: 91,
      compressorStatus: 'normal',
      alarms: [],
      inletTemp: 12.2,
      outletTemp: 6.8,
      ambientTemp: 24.3,
      powerConsumption: 139.2,
      coolingOutput: 490.5,
      cop: 3.52,
      runtime: 3500,
      fanSpeed: 65
    },
    "2": {
      name: `Chiller ${id}`,
      manufacturer: 'CarrierTech',
      model: 'CT-5500',
      status: 'healthy',
      lastMaintenance: '2023-11-05',
      nextMaintenance: '2024-05-05',
      refrigerantLevel: 89,
      compressorStatus: 'normal',
      alarms: [],
      inletTemp: 12.6,
      outletTemp: 6.5,
      ambientTemp: 24.8,
      powerConsumption: 144.6,
      coolingOutput: 522.1,
      cop: 3.61,
      runtime: 3130,
      fanSpeed: 72
    },
    "3": {
      name: `Chiller ${id}`,
      manufacturer: 'CarrierTech',
      model: 'CT-5500',
      status: 'attention',
      lastMaintenance: '2023-10-10',
      nextMaintenance: '2024-04-10',
      refrigerantLevel: 94,
      compressorStatus: 'normal',
      alarms: [],
      inletTemp: 12.5,
      outletTemp: 6.9,
      ambientTemp: 25.1,
      powerConsumption: 137.8,
      coolingOutput: 480.4,
      cop: 3.49,
      runtime: 2945,
      fanSpeed: 63
    }
  };

  const data = chillerData[id as keyof typeof chillerData] || chillerData["1"];

  // Generate health status display
  const getHealthStatus = () => {
    if (data.status === 'warning') return { status: 'warning', color: 'bg-amber-500', icon: <AlertTriangle className="h-4 w-4" /> };
    if (data.status === 'attention') return { status: 'attention', color: 'bg-amber-400', icon: <AlertTriangle className="h-4 w-4" /> };
    return { status: 'healthy', color: 'bg-emerald-500', icon: <Check className="h-4 w-4" /> };
  };

  const healthStatus = getHealthStatus();

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex justify-between items-center">
          <CardTitle>{data.name}</CardTitle>
          <div className={`flex items-center justify-center h-6 px-2 rounded-full ${healthStatus.color} text-white text-xs`}>
            <span className="mr-1">{healthStatus.icon}</span>
            <span>{healthStatus.status === 'attention' ? 'Attention' : healthStatus.status}</span>
          </div>
        </div>
        <div className="text-sm text-muted-foreground">
          {data.manufacturer} {data.model}
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <h4 className="text-sm font-medium">Performance</h4>
            <div className="grid grid-cols-2 gap-2 mt-2">
              <div>
                <p className="text-xs text-muted-foreground">Power</p>
                <p className="font-medium">{data.powerConsumption} kW</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Output</p>
                <p className="font-medium">{data.coolingOutput} kW</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">COP</p>
                <p className="font-medium">{data.cop}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Fan Speed</p>
                <p className="font-medium">{data.fanSpeed}%</p>
              </div>
            </div>
          </div>
          
          <div>
            <h4 className="text-sm font-medium">Temperature</h4>
            <div className="grid grid-cols-2 gap-2 mt-2">
              <div>
                <p className="text-xs text-muted-foreground">Inlet</p>
                <p className="font-medium">{data.inletTemp}°C</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Outlet</p>
                <p className="font-medium">{data.outletTemp}°C</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Ambient</p>
                <p className="font-medium">{data.ambientTemp}°C</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">ΔT</p>
                <p className="font-medium">{(data.inletTemp - data.outletTemp).toFixed(1)}°C</p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="mt-4 border-t pt-3">
          <h4 className="text-sm font-medium mb-2">System Status</h4>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <p className="text-xs text-muted-foreground">Refrigerant Level</p>
              <p className="font-medium">{data.refrigerantLevel}%</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Compressor</p>
              <p className="font-medium capitalize">{data.compressorStatus}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Runtime</p>
              <p className="font-medium">{data.runtime.toLocaleString()} hrs</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Next Maintenance</p>
              <p className="font-medium">{new Date(data.nextMaintenance).toLocaleDateString()}</p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ChillerDetailCard;
